import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../apiConfig';

const UploadItems = () => {
  const { user, checkAuth } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    roomType: '',
    roomStyle: '',
    furnitureType: '',
    additionalImages: [], // New field for multiple images
    // New fields for Shopify Flow 1
    productType: '',
    vendor: '',
    optionName: 'Finish', // Default option name, but user can change it
    finishVariants: [
      { name: '', price: '', compareAtPrice: '', stock: '', image: null, shopifyImages: [], colorScheme: '' }
    ]
  });
  
  const [dragActive, setDragActive] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [message, setMessage] = useState({ text: '', type: '', visible: false });
  const [csrfToken, setCsrfToken] = useState('');
  
  // Upload progress states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'uploading', 'success', 'error'

  // Shopify product creation state
  const [shopifyProductId, setShopifyProductId] = useState(null);
  const [shopifyOptionId, setShopifyOptionId] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(false);

  // State for dropdown options
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomStyles, setRoomStyles] = useState([]);
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [colorSchemes, setColorSchemes] = useState([]);

  // Check authentication status
  const handleAuthCheck = useCallback(() => {
    if (!checkAuth()) {
      return false;
    }
    return true;
  }, [checkAuth]);

  // Handle multiple image uploads
  const handleMultipleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxImages = 5; // Maximum 5 additional images
    
    if (formData.additionalImages.length + files.length > maxImages) {
      showMessage(`You can only upload a maximum of ${maxImages} additional images.`, 'error');
      return;
    }
    
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      showMessage('Some files were rejected. Only JPG, PNG, GIF files under 5MB are allowed.', 'error');
    }

    setFormData(prev => ({
      ...prev,
      additionalImages: [...prev.additionalImages, ...validFiles]
    }));
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
  };

  // Add function to remove finish variant
  const removeFinishVariant = (index) => {
    if (formData.finishVariants.length > 1) {
      setFormData(prev => ({
        ...prev,
        finishVariants: prev.finishVariants.filter((_, i) => i !== index)
      }));
    }
  };

  // Add function to add finish variant
  const addFinishVariant = () => {
    setFormData(prev => ({
      ...prev,
      finishVariants: [...prev.finishVariants, { name: '', price: '', compareAtPrice: '', stock: '', image: null, shopifyImages: [], colorScheme: '' }]
    }));
  };

  // Handle variant image upload
  const handleVariantImageUpload = (variantIndex, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newVariants = [...formData.finishVariants];
        newVariants[variantIndex].image = file;
        setFormData(prev => ({ ...prev, finishVariants: newVariants }));
      };
      reader.readAsDataURL(file);
    } else {
      showMessage('Please upload a valid image file', 'error');
    }
  };

  // Remove variant image
  const removeVariantImage = (variantIndex) => {
    const newVariants = [...formData.finishVariants];
    newVariants[variantIndex].image = null;
    setFormData(prev => ({ ...prev, finishVariants: newVariants }));
  };

  // Handle variant Shopify images upload
  const handleVariantShopifyImagesUpload = (variantIndex, files) => {
    const fileArray = Array.from(files);
    const maxImages = 4; // Maximum 4 Shopify images per variant
    
    const currentImages = formData.finishVariants[variantIndex].shopifyImages || [];
    
    if (currentImages.length + fileArray.length > maxImages) {
      showMessage(`You can only upload a maximum of ${maxImages} Shopify images per variant.`, 'error');
      return;
    }
    
    const validFiles = fileArray.filter(file => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== fileArray.length) {
      showMessage('Some files were rejected. Only JPG, PNG files under 5MB are allowed.', 'error');
    }

    const newVariants = [...formData.finishVariants];
    newVariants[variantIndex].shopifyImages = [...currentImages, ...validFiles];
    setFormData(prev => ({ ...prev, finishVariants: newVariants }));
  };

  // Remove variant Shopify image
  const removeVariantShopifyImage = (variantIndex, imageIndex) => {
    const newVariants = [...formData.finishVariants];
    newVariants[variantIndex].shopifyImages = newVariants[variantIndex].shopifyImages.filter((_, i) => i !== imageIndex);
    setFormData(prev => ({ ...prev, finishVariants: newVariants }));
  };

  const handleMultipleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const maxImages = 5; // Maximum 5 additional images
    
    if (formData.additionalImages.length + files.length > maxImages) {
      showMessage(`You can only upload a maximum of ${maxImages} additional images.`, 'error');
      return;
    }

    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      showMessage('Some files were rejected. Only JPG, PNG, GIF files under 5MB are allowed.', 'error');
    }

    setFormData(prev => ({
      ...prev,
      additionalImages: [...prev.additionalImages, ...validFiles]
    }));
  };

  // Check authentication on component mount
  useEffect(() => {
    if (!handleAuthCheck()) {
      window.location.href = '/login';
      return;
    }
  }, [handleAuthCheck]);

  // Get CSRF token on component mount
  useEffect(() => {
    const getCsrfToken = async () => {
      try {
        const response = await fetch(apiUrl('/api/get-csrf-token/'), {
          credentials: 'include',
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        console.error('Failed to get CSRF token:', error);
      }
    };

    getCsrfToken();
  }, []);

  // Fetch dropdown options on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // First check if user is authenticated
        if (!user) {
          showMessage('Please log in to access this page', 'error');
          return;
        }

        const [typesRes, stylesRes, furnitureTypesRes, schemesRes] = await Promise.all([
          fetch(apiUrl('/api/room-types/'), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRFToken': csrfToken,
              'Content-Type': 'application/json',
            },
          }),
          fetch(apiUrl('/api/room-styles/'), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRFToken': csrfToken,
              'Content-Type': 'application/json',
            },
          }),
          fetch(apiUrl('/api/furniture-types/'), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRFToken': csrfToken,
              'Content-Type': 'application/json',
            },
          }),
          fetch(apiUrl('/api/color-schemes/'), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRFToken': csrfToken,
              'Content-Type': 'application/json',
            },
          })
        ]);

        // Check if any request failed due to authentication
        if (typesRes.status === 401 || stylesRes.status === 401 || furnitureTypesRes.status === 401 || schemesRes.status === 401) {
          showMessage('Authentication expired. Please log in again.', 'error');
          checkAuth();
          return;
        }

        if (!typesRes.ok || !stylesRes.ok || !furnitureTypesRes.ok || !schemesRes.ok) {
          throw new Error('Failed to fetch options');
        }

        const typesData = await typesRes.json();
        const stylesData = await stylesRes.json();
        const furnitureTypesData = await furnitureTypesRes.json();
        const schemesData = await schemesRes.json();

        setRoomTypes([
          { value: '', label: 'Select Room Type' },
          ...typesData.map(type => ({ value: type.id, label: type.type }))
        ]);

        setRoomStyles([
          { value: '', label: 'Select Room Style' },
          ...stylesData.map(style => ({
            value: style.id,
            label: style.style
          }))
        ]);

        setFurnitureTypes([
          { value: '', label: 'Select Furniture Type' },
          ...furnitureTypesData.types.map(type => ({
            value: type.id,
            label: type.name
          }))
        ]);

        setColorSchemes([
          { value: '', label: 'Select Color Scheme' },
          ...schemesData.schemes.map(scheme => ({
            value: scheme.id,
            label: scheme.name
          }))
        ]);
      } catch (error) {
        console.error('Error fetching options:', error);
        showMessage('Failed to load form options. Please try again.', 'error');
      }
    };

    // Only fetch options if we have a CSRF token
    if (csrfToken) {
      fetchOptions();
    }
  }, [csrfToken, checkAuth, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = (fieldName) => {
    if (!formData[fieldName]) {
      setFocusedField('');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  // Step 1: Create empty product in Shopify
  const handleCreateShopifyProduct = async () => {
    // Validation for product info only
    const requiredFields = ['name', 'description', 'productType', 'vendor'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      showMessage(`Please fill in: ${missingFields.join(', ')}`, 'error');
      return;
    }

    setCreatingProduct(true);

    try {
      const response = await fetch(apiUrl('/api/create-shopify-product/'), {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          productType: formData.productType,
          vendor: formData.vendor,
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to create Shopify product');
      }

      const data = await response.json();
      
      if (data.success) {
        setShopifyProductId(data.product_id);
        setShopifyOptionId(data.option_id);
        showMessage(`Product created in Shopify! ID: ${data.product_id}`, 'success');
      } else {
        throw new Error(data.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating Shopify product:', error);
      showMessage(error.message || 'Failed to create Shopify product', 'error');
    } finally {
      setCreatingProduct(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Must have Shopify product created first
    if (!shopifyProductId || !shopifyOptionId) {
      showMessage('Please complete Step 1 first: Create Product in Shopify', 'error');
      return;
    }
    
    // Validation
    const requiredFields = ['name', 'description', 'price', 'roomType', 'roomStyle', 'furnitureType', 'productType', 'optionName'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      showMessage(`Please fill in: ${missingFields.join(', ')}`, 'error');
      return;
    }

    // Validate finish variants - now includes colorScheme validation
    const validVariants = formData.finishVariants.filter(variant => 
      variant.name && variant.price && parseFloat(variant.price) > 0 && variant.image && variant.colorScheme
    );
    
    console.log('Variant validation:', {
      totalVariants: formData.finishVariants.length,
      validVariants: validVariants.length,
      variantDetails: formData.finishVariants.map(v => ({
        name: v.name,
        hasPrice: !!v.price,
        hasImage: !!v.image,
        hasColorScheme: !!v.colorScheme,
        shopifyImagesCount: v.shopifyImages?.length || 0
      }))
    });
    
    // Check if any variants are missing required fields
    const invalidVariants = formData.finishVariants.filter(variant => 
      variant.name || variant.price || variant.image // Has some data but incomplete
    ).filter(variant => 
      !variant.name || !variant.price || parseFloat(variant.price) <= 0 || !variant.image || !variant.colorScheme
    );
    
    if (invalidVariants.length > 0) {
      console.error('Invalid variants detected:', invalidVariants);
      const missingColorScheme = invalidVariants.filter(v => !v.colorScheme && v.name);
      if (missingColorScheme.length > 0) {
        showMessage(`Please select a color scheme for: ${missingColorScheme.map(v => v.name || 'unnamed variant').join(', ')}`, 'error');
        return;
      }
      showMessage('Please complete all variant fields (name, price, color scheme, and app image)', 'error');
      return;
    }
    
    if (validVariants.length === 0) {
      console.error('No valid variants found');
      showMessage('Please add at least one variant with name, price, color scheme, and app image (PNG)', 'error');
      return;
    }

    // Stock validation: optional, but if typed must be a non-negative integer.
    // Empty string => skip the inventory push for this variant on the backend.
    const invalidStockVariants = formData.finishVariants
      .map((v, idx) => ({ v, idx }))
      .filter(({ v }) => v.stock !== '' && v.stock !== null && v.stock !== undefined)
      .filter(({ v }) => {
        const n = Number(v.stock);
        return !Number.isInteger(n) || n < 0;
      });
    if (invalidStockVariants.length > 0) {
      const names = invalidStockVariants.map(({ v }) => v.name || 'unnamed variant').join(', ');
      showMessage(`Stock must be a whole non-negative number for: ${names}`, 'error');
      return;
    }

    console.log('✓ Validation passed');
    console.log('Preparing FormData...');

    // Create form data for multipart/form-data submission
    const submitData = new FormData();
    
    // Append variant images (PNG for app)
    validVariants.forEach((variant, index) => {
      console.log(`Appending variant APP image ${index}:`, {
        name: variant.image?.name,
        size: variant.image?.size,
        type: variant.image?.type
      });
      submitData.append(`variant_image_${index}`, variant.image);
    });
    
    // Append variant Shopify images (JPG for Shopify store)
    validVariants.forEach((variant, index) => {
      variant.shopifyImages.forEach((file, imgIndex) => {
        console.log(`Appending variant SHOPIFY image ${index}-${imgIndex}:`, {
          name: file.name,
          size: file.size,
          type: file.type
        });
        submitData.append(`variant_${index}_shopify_image_${imgIndex}`, file);
      });
    });
    
    // Append additional images
    formData.additionalImages.forEach((file, index) => {
      console.log(`Appending additional image ${index}:`, {
        name: file.name,
        size: file.size,
        type: file.type
      });
      submitData.append(`additional_images`, file);
    });
    
    const jsonData = {
      name: formData.name,
      description: formData.description,
      price: formData.price,
      roomType: formData.roomType,
      roomStyle: formData.roomStyle,
      furnitureType: formData.furnitureType,
      productType: formData.productType,
      vendor: formData.vendor,
      optionName: formData.optionName,
      finishVariants: validVariants.map(v => ({
        name: v.name,
        price: v.price,
        compareAtPrice: v.compareAtPrice,
        colorScheme: v.colorScheme,  // Each variant now has its own colorScheme
        // Optional initial stock: backend pushes to Shopify in Phase 5.
        // Empty => omit so backend skips inventory push for this variant.
        ...(v.stock !== '' && v.stock !== null && v.stock !== undefined
          ? { inventoryQuantity: parseInt(v.stock, 10) }
          : {}),
      })),
      shopifyProductId: shopifyProductId,  // Pass the Shopify product ID if it exists
      optionId: shopifyOptionId  // Pass the option ID from Step 1
    };
    
    console.log('JSON data to send:', jsonData);
    submitData.append('data', JSON.stringify(jsonData));
    
    // Log FormData contents (for debugging)
    console.log('FormData entries:');
    for (let pair of submitData.entries()) {
      if (pair[1] instanceof File) {
        console.log(`  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes, ${pair[1].type})`);
      } else {
        console.log(`  ${pair[0]}: ${typeof pair[1] === 'string' && pair[1].length > 100 ? pair[1].substring(0, 100) + '...' : pair[1]}`);
      }
    }

    // Declare progressInterval outside try block so catch can access it
    let progressInterval;

    try {
      console.log('Opening upload modal and starting progress...');
      // Open upload modal and start progress
      setUploadModalOpen(true);
      setUploadStage('uploading');
      setUploadProgress(0);
      
      // Simulate progress (since we can't track actual upload progress easily)
      progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);
      
      // Create AbortController with 5 minute timeout for slow networks
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes
      
      console.log('Making POST request to /api/upload-furniture/');
      console.log('Request details:', {
        url: apiUrl('/api/upload-furniture/'),
        method: 'POST',
        hasCsrfToken: !!csrfToken,
        csrfTokenLength: csrfToken?.length || 0,
        credentials: 'include',
        formDataKeys: Array.from(submitData.keys())
      });
      
      const requestStartTime = Date.now();
      const response = await fetch(apiUrl('/api/upload-furniture/'), {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        body: submitData,
        credentials: 'include',
        signal: controller.signal
      });
      
      const requestDuration = Date.now() - requestStartTime;
      console.log(`Request completed in ${requestDuration}ms`);
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      clearTimeout(timeoutId);

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Request failed with status:', response.status);
        console.error('Error response body:', errorText);
        try {
          const errorJson = JSON.parse(errorText);
          console.error('Error JSON:', errorJson);
          throw new Error(errorJson.detail || errorJson.error || 'Failed to upload item');
        } catch (parseError) {
          console.error('Could not parse error response as JSON:', parseError);
          throw new Error(`Failed to upload item: ${response.status} ${response.statusText}`);
        }
      }

      const responseData = await response.json();
      console.log('✓ Upload successful!');
      console.log('Response data:', responseData);
      
      // Complete progress and show success
      setUploadProgress(100);
      setUploadStage('success');
      
      // Wait for success animation, then reset
      setTimeout(() => {
        setUploadModalOpen(false);
        setUploadProgress(0);
        setUploadStage('');
        
        // Reset form
        setFormData({
          name: '',
          description: '',
          price: '',
          roomType: '',
          roomStyle: '',
          furnitureType: '',
          additionalImages: [],
          productType: '',
          vendor: '',
          optionName: 'Finish',
          finishVariants: [
            { name: '', price: '', compareAtPrice: '', stock: '', image: null, shopifyImages: [], colorScheme: '' }
          ]
        });
        setFocusedField('');
        
        // Reset Shopify product states to show Step 1 button again
        setShopifyProductId(null);
        setShopifyOptionId(null);
        setCreatingProduct(false);
        
        console.log('=== STEP 2: UPLOAD COMPLETED SUCCESSFULLY ===');
      }, 2500);
      
    } catch (error) {
      console.error('=== STEP 2: UPLOAD ERROR ===');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (error.name === 'AbortError') {
        console.error('Request was aborted (timeout)');
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error - fetch failed');
      }
      
      clearInterval(progressInterval);
      setUploadStage('error');
      
      let errorMessage = error.message;
      if (error.name === 'AbortError') {
        errorMessage = 'Upload timed out. Please check your internet connection and try again with smaller images.';
      }
      
      setTimeout(() => {
        setUploadModalOpen(false);
        setUploadProgress(0);
        setUploadStage('');
        showMessage(errorMessage, 'error');
        console.log('=== STEP 2: ERROR HANDLING COMPLETE ===');
      }, 2000);
    }
  };

  return (
    <div className="bg-[#DFDAD3] min-h-full flex flex-col">
      {/* Upload Progress Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-96 transform transition-all">
            {uploadStage === 'uploading' && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#2C2C2C] to-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <svg className="w-10 h-10 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">Uploading...</h3>
                  <p className="text-[#666]">Please wait while we upload your product</p>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-[#E8E6E1] rounded-full h-3 overflow-hidden mb-3">
                  <div 
                    className="bg-gradient-to-r from-[#2C2C2C] to-[#1a1a1a] h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-[#666] font-medium">{uploadProgress}%</p>
              </div>
            )}

            {uploadStage === 'success' && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">Success!</h3>
                  <p className="text-[#666]">Your product has been uploaded successfully</p>
                </div>
              </div>
            )}

            {uploadStage === 'error' && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">Upload Failed</h3>
                  <p className="text-[#666]">Please try again</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success/Error Message */}
      {message.visible && (
        <div className={`fixed top-24 right-8 px-6 py-4 rounded-xl shadow-lg ${
          message.type === 'error' ? 'bg-red-500 text-white' :
          message.type === 'success' ? 'bg-green-500 text-white' :
          'bg-[#2C2C2C] text-white'
        } text-sm font-medium z-50 animate-fade-in`}>
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 px-8 py-12 flex justify-center">
        <div className="w-full max-w-[720px]">
          {/* Title Section */}
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-light text-[#2C2C2C] tracking-tight mb-4">UPLOAD</h2>
            <p className="text-[#666] text-lg">Add new furniture items to your catalog</p>
          </div>

          {/* Upload Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* SECTION 1: PRODUCT INFORMATION */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E8E6E1]">
              <div className="mb-6">
                <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">Product Information</h3>
                <p className="text-sm text-[#666]">
                  Basic product details that will be used in both the app catalog and Shopify store
                </p>
              </div>

              <div className="space-y-8">
                {/* Name Field */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('name')}
                    onBlur={() => handleBlur('name')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'name' || formData.name 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                    placeholder="Enter product name"
                  />
                </div>

                {/* Description Field */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('description')}
                    onBlur={() => handleBlur('description')}
                    rows={3}
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left resize-none ${
                      focusedField === 'description' || formData.description 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                    placeholder="Enter product description"
                  />
                </div>

                {/* Price Field */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Base Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('price')}
                    onBlur={() => handleBlur('price')}
                    required
                    min="0"
                    step="0.01"
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'price' || formData.price 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                    placeholder="Enter base price"
                  />
                </div>

                {/* Product Type */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Product Type
                  </label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('productType')}
                    onBlur={() => handleBlur('productType')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'productType' || formData.productType 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                  >
                    <option value="">Select Product Type</option>
                    <option value="Furniture">Furniture</option>
                    <option value="Accessory">Accessory</option>
                    <option value="Decor">Decor</option>
                  </select>
                </div>

                {/* Vendor */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Vendor
                  </label>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('vendor')}
                    onBlur={() => handleBlur('vendor')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'vendor' || formData.vendor 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                    placeholder="Enter vendor name"
                  />
                </div>

                {/* Room Type */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Room Type
                  </label>
                  <select
                    name="roomType"
                    value={formData.roomType}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('roomType')}
                    onBlur={() => handleBlur('roomType')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'roomType' || formData.roomType 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                  >
                    {roomTypes.map(type => (
                      <option key={type.value} value={type.value} className="text-[#2C2C2C] bg-[#DFDAD3]">
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Room Style */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Room Style
                  </label>
                  <select
                    name="roomStyle"
                    value={formData.roomStyle}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('roomStyle')}
                    onBlur={() => handleBlur('roomStyle')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'roomStyle' || formData.roomStyle 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                  >
                    {roomStyles.map(style => (
                      <option key={style.value} value={style.value} className="text-[#2C2C2C] bg-[#DFDAD3]">
                        {style.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Furniture Type */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Furniture Type
                  </label>
                  <select
                    name="furnitureType"
                    value={formData.furnitureType}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('furnitureType')}
                    onBlur={() => handleBlur('furnitureType')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'furnitureType' || formData.furnitureType 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC2C]'
                    }`}
                  >
                    {furnitureTypes.map(type => (
                      <option key={type.value} value={type.value} className="text-[#2C2C2C] bg-[#DFDAD3]">
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
            </div>

            {/* SECTION 2: VARIANTS & MEDIA */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E8E6E1]">
              <div className="mb-6">
                <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">Variants & Media</h3>
                <p className="text-sm text-[#666]">
                  Configure product variants with individual pricing and images. Each variant requires 1 PNG for the app, and optionally 1-4 JPGs for Shopify.
                </p>
              </div>

              <div className="space-y-8">
                {/* Option Name Field */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Option Name (e.g., "Finish", "Color", "Material")
                  </label>
                  <input
                    type="text"
                    name="optionName"
                    value={formData.optionName}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('optionName')}
                    onBlur={() => handleBlur('optionName')}
                    required
                    className={`w-full bg-transparent border-0 border-b py-5 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                      focusedField === 'optionName' || formData.optionName 
                        ? 'border-[#2C2C2C] border-b-2' 
                        : 'border-[#D1CFC8]'
                    }`}
                    placeholder="Enter option name (e.g., Finish, Color, Material)"
                  />
                </div>

                {/* Finish Variants */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Variants
                  </label>
                  <p className="text-sm text-[#666] mb-4">
                    Each variant requires 1 PNG image (transparent, for app) and optionally 1-4 JPG images (styled, for Shopify store). 
                    Each variant creates a separate database record, while Shopify gets one product with multiple variants.
                  </p>
                  {formData.finishVariants.map((variant, index) => (
                    <div key={index} className="border border-[#E8E6E1] rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                            Variant Name
                          </label>
                          <input
                            type="text"
                            name={`finishVariantName_${index}`}
                            value={variant.name}
                            onChange={(e) => {
                              const newVariants = [...formData.finishVariants];
                              newVariants[index].name = e.target.value;
                              setFormData(prev => ({ ...prev, finishVariants: newVariants }));
                            }}
                            onFocus={() => handleFocus(`finishVariantName_${index}`)}
                            onBlur={() => handleBlur(`finishVariantName_${index}`)}
                            placeholder="e.g., Blue, Red, Yellow"
                            className={`w-full bg-transparent border-0 border-b py-3 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                              focusedField === `finishVariantName_${index}` || variant.name 
                                ? 'border-[#2C2C2C] border-b-2' 
                                : 'border-[#D1CFC8]'
                            }`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                            Price
                          </label>
                          <input
                            type="number"
                            name={`finishVariantPrice_${index}`}
                            value={variant.price}
                            onChange={(e) => {
                              const newVariants = [...formData.finishVariants];
                              newVariants[index].price = e.target.value;
                              setFormData(prev => ({ ...prev, finishVariants: newVariants }));
                            }}
                            onFocus={() => handleFocus(`finishVariantPrice_${index}`)}
                            onBlur={() => handleBlur(`finishVariantPrice_${index}`)}
                            placeholder="Price"
                            min="0"
                            step="0.01"
                            className={`w-full bg-transparent border-0 border-b py-3 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                              focusedField === `finishVariantPrice_${index}` || variant.price 
                                ? 'border-[#2C2C2C] border-b-2' 
                                : 'border-[#D1CFC8]'
                            }`}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                            Compare At Price (Optional)
                          </label>
                          <input
                            type="number"
                            name={`finishVariantCompareAtPrice_${index}`}
                            value={variant.compareAtPrice}
                            onChange={(e) => {
                              const newVariants = [...formData.finishVariants];
                              newVariants[index].compareAtPrice = e.target.value;
                              setFormData(prev => ({ ...prev, finishVariants: newVariants }));
                            }}
                            onFocus={() => handleFocus(`finishVariantCompareAtPrice_${index}`)}
                            onBlur={() => handleBlur(`finishVariantCompareAtPrice_${index}`)}
                            placeholder="Compare At Price"
                            min="0"
                            step="0.01"
                            className={`w-full bg-transparent border-0 border-b py-3 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                              focusedField === `finishVariantCompareAtPrice_${index}` || variant.compareAtPrice 
                                ? 'border-[#2C2C2C] border-b-2' 
                                : 'border-[#D1CFC8]'
                            }`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                            Initial Stock (Optional)
                          </label>
                          <input
                            type="number"
                            name={`finishVariantStock_${index}`}
                            value={variant.stock}
                            onChange={(e) => {
                              const newVariants = [...formData.finishVariants];
                              newVariants[index].stock = e.target.value;
                              setFormData(prev => ({ ...prev, finishVariants: newVariants }));
                            }}
                            onFocus={() => handleFocus(`finishVariantStock_${index}`)}
                            onBlur={() => handleBlur(`finishVariantStock_${index}`)}
                            placeholder="Stock quantity"
                            min="0"
                            step="1"
                            className={`w-full bg-transparent border-0 border-b py-3 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                              focusedField === `finishVariantStock_${index}` || variant.stock !== ''
                                ? 'border-[#2C2C2C] border-b-2'
                                : 'border-[#D1CFC8]'
                            }`}
                          />
                          <p className="text-xs text-[#999] mt-1">
                            Leave blank to skip pushing stock to Shopify (set later in Edit).
                          </p>
                        </div>
                      </div>

                      {/* Color Scheme - Per Variant */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                          Color Scheme (App Category) <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-[#666] mb-2">
                          Select the color scheme for organizing this variant in the app
                        </p>
                        <select
                          name={`finishVariantColorScheme_${index}`}
                          value={variant.colorScheme}
                          onChange={(e) => {
                            const newVariants = [...formData.finishVariants];
                            newVariants[index].colorScheme = e.target.value;
                            setFormData(prev => ({ ...prev, finishVariants: newVariants }));
                          }}
                          onFocus={() => handleFocus(`finishVariantColorScheme_${index}`)}
                          onBlur={() => handleBlur(`finishVariantColorScheme_${index}`)}
                          className={`w-full bg-transparent border-0 border-b py-3 px-0 text-base text-[#2C2C2C] focus:outline-none transition-colors text-left ${
                            focusedField === `finishVariantColorScheme_${index}` || variant.colorScheme 
                              ? 'border-[#2C2C2C] border-b-2' 
                              : 'border-[#D1CFC8]'
                          }`}
                        >
                          {colorSchemes.map(scheme => (
                            <option key={scheme.value} value={scheme.value} className="text-[#2C2C2C] bg-[#DFDAD3]">
                              {scheme.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Variant Image Upload (PNG for App) */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                          App Image (PNG - Transparent Background)
                        </label>
                        <p className="text-xs text-[#666] mb-2">
                          Upload 1 PNG image with transparent background for the mobile app catalog
                        </p>
                        
                        {!variant.image ? (
                          <div
                            className={`w-full h-32 border-2 border-dashed transition-colors cursor-pointer ${
                              dragActive 
                                ? 'border-[#2C2C2C] bg-[#2C2C2C]/5' 
                                : 'border-[#D1CFC8] hover:border-[#2C2C2C] hover:bg-[#2C2C2C]/5'
                            }`}
                            onClick={() => document.getElementById(`variant-image-input-${index}`)?.click()}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragActive(false);
                              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                handleVariantImageUpload(index, e.dataTransfer.files[0]);
                              }
                            }}
                          >
                            <div className="flex flex-col items-center justify-center h-full p-4">
                              <p className="text-[#666] text-center text-sm">
                                Drag & drop or click to upload
                              </p>
                              <p className="text-[#999] text-xs text-center">
                                JPG, PNG, GIF (max 5MB)
                              </p>
                            </div>
                            <input
                              id={`variant-image-input-${index}`}
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleVariantImageUpload(index, e.target.files[0]);
                                }
                              }}
                              accept="image/*"
                            />
                          </div>
                        ) : (
                          <div className="relative w-full h-32 bg-[#2C2C2C]/5 rounded-lg overflow-hidden">
                            <img
                              src={URL.createObjectURL(variant.image)}
                              alt={`Variant ${index + 1}`}
                              className="w-full h-full object-contain"
                            />
                            <button
                              type="button"
                              onClick={() => removeVariantImage(index)}
                              className="absolute top-2 right-2 bg-[#2C2C2C] text-white p-2 rounded-full hover:bg-[#1a1a1a] transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <p className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                              {variant.image.name}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Variant Shopify Images Upload (JPG for Shopify) */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-[#2C2C2C] mb-2 uppercase tracking-wide">
                          Shopify Images (JPG - Styled Photos) - Optional
                        </label>
                        <p className="text-xs text-[#666] mb-2">
                          Upload 1-4 styled JPG images for this variant on Shopify store (optional)
                        </p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-[#666]">
                            {variant.shopifyImages ? variant.shopifyImages.length : 0}/4 images
                          </span>
                        </div>
                        
                        {/* Shopify Images Drop Zone */}
                        {(!variant.shopifyImages || variant.shopifyImages.length < 4) && (
                          <div
                            className="w-full h-24 border-2 border-dashed border-[#D1CFC8] hover:border-[#2C2C2C] hover:bg-[#2C2C2C]/5 transition-colors cursor-pointer mb-3"
                            onClick={() => document.getElementById(`variant-shopify-images-input-${index}`)?.click()}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragActive(false);
                              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                handleVariantShopifyImagesUpload(index, e.dataTransfer.files);
                              }
                            }}
                          >
                            <div className="flex flex-col items-center justify-center h-full p-3">
                              <p className="text-[#666] text-center text-xs mb-1">
                                Drag & drop or click to upload Shopify images
                              </p>
                              <p className="text-[#999] text-xs text-center">
                                JPG, PNG (max 5MB each, 1-4 images)
                              </p>
                            </div>
                            <input
                              id={`variant-shopify-images-input-${index}`}
                              type="file"
                              className="hidden"
                              multiple
                              onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                  handleVariantShopifyImagesUpload(index, e.target.files);
                                }
                              }}
                              accept="image/jpeg,image/jpg,image/png"
                            />
                          </div>
                        )}

                        {/* Display Uploaded Shopify Images */}
                        {variant.shopifyImages && variant.shopifyImages.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {variant.shopifyImages.map((file, imgIndex) => (
                              <div key={imgIndex} className="relative group">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Shopify ${imgIndex + 1}`}
                                  className="w-full h-20 object-cover rounded border border-[#E8E6E1]"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeVariantShopifyImage(index, imgIndex)}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                <p className="text-xs text-[#666] mt-1 truncate">{file.name}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Remove Variant Button */}
                      {formData.finishVariants.length > 1 && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeFinishVariant(index)}
                            className="text-red-500 hover:text-red-700 transition-colors text-sm"
                          >
                            Remove Variant
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addFinishVariant}
                    className="text-sm text-[#666] hover:text-[#2C2C2C] transition-colors mt-4"
                  >
                    + Add Finish Variant
                  </button>
                </div>

                {/* Additional Images Upload */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-3 uppercase tracking-wide text-left">
                    Shopify Images
                  </label>
                  <p className="text-sm text-[#666] mb-3">
                    These images will be sent to Shopify to create the product with multiple views and angles.
                  </p>
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm text-[#666]">Upload multiple images for different angles or views</p>
                    <span className="text-sm text-[#666]">
                      {formData.additionalImages.length}/5 images
                    </span>
                  </div>
                  
                  {/* Multiple Images Drop Zone */}
                  <div
                    className={`w-full h-32 border-2 border-dashed transition-colors cursor-pointer ${
                      formData.additionalImages.length >= 5 
                        ? 'border-[#D1CFC8] bg-gray-50 cursor-not-allowed' 
                        : dragActive 
                          ? 'border-[#2C2C2C] bg-[#2C2C2C]/5' 
                          : 'border-[#D1CFC8] hover:border-[#2C2C2C] hover:bg-[#2C2C2C]/5'
                    }`}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (formData.additionalImages.length < 5) setDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={formData.additionalImages.length >= 5 ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleMultipleImageDrop}
                    onClick={formData.additionalImages.length >= 5 ? undefined : () => document.getElementById('multiple-images-input')?.click()}
                  >
                    <div className="flex flex-col items-center justify-center h-full p-4">
                      {formData.additionalImages.length >= 5 ? (
                        <p className="text-[#666] text-center">Maximum number of additional images reached</p>
                      ) : (
                        <>
                          <p className="text-[#666] text-center mb-2">
                            Drag & drop multiple images here, or click to select
                          </p>
                          <p className="text-[#999] text-sm text-center">
                            Supported formats: JPG, PNG, GIF (max 5MB each)
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      id="multiple-images-input"
                      type="file"
                      className="hidden"
                      multiple
                      onChange={handleMultipleImageSelect}
                      accept="image/*"
                      disabled={formData.additionalImages.length >= 5}
                    />
                  </div>

                  {/* Display Uploaded Additional Images */}
                  {formData.additionalImages.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-[#2C2C2C] mb-2">Uploaded Additional Images:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {formData.additionalImages.map((file, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Additional ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-[#E8E6E1]"
                            />
                            <button
                              type="button"
                              onClick={() => removeAdditionalImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <p className="text-xs text-[#666] mt-1 truncate">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Shopify Product Creation and Submit Buttons */}
            <div className="space-y-4 mt-8">
              {/* Step 1: Create Product in Shopify */}
              {!shopifyProductId && (
                <button 
                  type="button"
                  onClick={handleCreateShopifyProduct}
                  disabled={creatingProduct}
                  className="w-full bg-blue-600 text-white py-4 px-8 text-sm font-medium uppercase tracking-wide hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {creatingProduct ? 'Creating Product in Shopify...' : 'Step 1: Create Product in Shopify'}
                </button>
              )}

              {/* Show product ID if created */}
              {shopifyProductId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-800 font-medium">✓ Product Created in Shopify</p>
                  <p className="text-xs text-green-600 mt-1 break-all">{shopifyProductId}</p>
                </div>
              )}

              {/* Step 2: Submit Everything */}
              <button 
                type="submit"
                disabled={!shopifyProductId}
                className="w-full bg-[#2C2C2C] text-white py-4 px-8 text-sm font-medium uppercase tracking-wide hover:bg-[#1a1a1a] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {shopifyProductId ? 'Step 2: Upload Images & Create Variants' : 'Create Product in Shopify First'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default UploadItems;